import React, { useState } from "react";
import { CriterionInput } from "../../services/rubricAdminService";
import CriterionDetail from "./CriterionDetail";

interface Props {
  rubricId: string;
  getToken: () => string | undefined;
  criteria: CriterionInput[];
  onChange: (next: CriterionInput[]) => void;
}

const KINDS: { key: "conversation" | "solution"; label: string }[] = [
  { key: "conversation", label: "Conversation criteria" },
  { key: "solution", label: "Solution criteria" },
];

const newCriterion = (kind: "conversation" | "solution"): CriterionInput => ({
  kind,
  name: "",
  description: "",
  instructions: "",
  weight: 1,
  performanceLevels: [],
});

const CriteriaEditor: React.FC<Props> = ({ rubricId, getToken, criteria, onChange }) => {
  const [selected, setSelected] = useState<number | null>(null);

  const pct = (c: CriterionInput) => {
    const sum = criteria
      .filter((k) => k.kind === c.kind)
      .reduce((s, k) => s + (k.weight || 0), 0);
    return sum > 0 ? Math.round(((c.weight || 0) / sum) * 100) : 0;
  };
  const add = (kind: "conversation" | "solution") => {
    onChange([...criteria, newCriterion(kind)]);
    setSelected(criteria.length);
  };
  const remove = (idx: number) => {
    onChange(criteria.filter((_, j) => j !== idx));
    setSelected(null);
  };

  return (
    <div className="border-t border-outline-variant/30 mt-6 pt-4 flex gap-4 min-h-[300px]">
      <div className="w-64 shrink-0 border-r border-outline-variant/30 pr-3">
        {KINDS.map(({ key, label }) => (
          <div key={key} className="mb-4">
            <div className="form-label">{label}</div>
            {criteria.map((c, i) =>
              c.kind === key ? (
                <button
                  key={i}
                  type="button"
                  className={`w-full text-left text-sm px-2 py-1 rounded ${selected === i ? "bg-primary text-white" : "text-slate-300"}`}
                  onClick={() => setSelected(i)}
                >
                  {c.name || "Untitled"} <span className="opacity-60">· {pct(c)}%</span>
                </button>
              ) : null,
            )}
            <button
              type="button"
              className="text-xs text-slate-400 mt-1"
              onClick={() => add(key)}
            >
              + Add criterion
            </button>
          </div>
        ))}
      </div>
      <div className="flex-1 min-w-0">
        {selected !== null && criteria[selected] ? (
          <>
            <div className="flex justify-end mb-2">
              <button
                type="button"
                className="text-error text-xs"
                onClick={() => remove(selected)}
              >
                Delete criterion
              </button>
            </div>
            <CriterionDetail
              rubricId={rubricId}
              getToken={getToken}
              value={criteria[selected]}
              onChange={(next) =>
                onChange(criteria.map((c, j) => (j === selected ? next : c)))
              }
            />
          </>
        ) : (
          <div className="text-slate-400 text-sm">Select or add a criterion to edit.</div>
        )}
      </div>
    </div>
  );
};

export default CriteriaEditor;
